import * as React from 'react';
import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';
import { User } from '@/types/user';
import type { Integration, IntegrationBootstrapConfig } from '../types';

// ============================================================================
// Sentry SDK Initialization
// ============================================================================

export interface InitializeSentryOptions {
  dsn: string;
  environment?: string;
}

/**
 * Low-level Sentry SDK initialization
 *
 * IMPORTANT: Call this BEFORE React renders to capture all startup errors.
 */
export const initializeSentry = ({
  dsn,
  environment,
}: InitializeSentryOptions): void => {
  // Avoid double-initialisation when main.tsx is re-imported (e.g. HMR)
  if (Sentry.getClient()) {
    return;
  }

  Sentry.init({
    dsn,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 0.01,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      'Load failed',
      /Blocked a frame/,
      "Cannot read properties of null (reading 'shadowRoot')",
      /Failed to fetch dynamically imported module/,
      /Importing a module script failed/,
      /Unable to preload CSS/,
    ],
  });
};

/**
 * Initialize Sentry based on integration configuration
 * Handles all conditional logic and error handling
 *
 * @param config - Integration bootstrap configuration
 * @returns Whether Sentry was initialized
 */
export function initializeSentryFromConfig(
  config: IntegrationBootstrapConfig
): boolean {
  // Only initialize in production
  if (!config.isProd) {
    return false;
  }

  // Check for required DSN
  if (!config.sentryDsn) {
    console.warn('[Sentry] Missing VITE_SENTRY_DSN in production environment');
    return false;
  }

  try {
    initializeSentry({
      dsn: config.sentryDsn,
      environment: config.environment,
    });
    console.log('[Sentry] Initialized successfully');
    return true;
  } catch (error) {
    console.error('[Sentry] Failed to initialize:', error);
    return false;
  }
}

// ============================================================================
// Sentry Integration (User Identification)
// ============================================================================

/**
 * Sentry Integration for User Identification
 *
 * Sentry SDK is initialized during bootstrap BEFORE React renders to capture startup errors.
 * This integration only handles post-login user identification and cleanup.
 */
export const createSentryIntegration = (): Integration => {
  let isIdentified = false;

  return Object.freeze({
    name: 'sentry',

    identify: (user: User): void => {
      Sentry.setUser({
        email: user.email,
        id: user.user_id,
        username: user.username,
      });
      isIdentified = true;
    },

    reset: (): void => {
      if (isIdentified) {
        Sentry.setUser(null);
        isIdentified = false;
      }
    },
  });
};
